import React, { useRef, useState } from 'react';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { jsPDF } from 'jspdf';
import { useReactToPrint } from 'react-to-print';
import {
  DONE_CODES,
  DOWNLOAD_CODES,
  ERROR_DOWNLOAD_CODES,
  GENERATE_NEW_CODES,
  PRINT_CODES,
  SUCCESS_DOWNLOAD_CODES,
} from 'common/constants/login';
import { useResponse } from 'services/hooks';
import {
  ActionButton,
  ButtonsGrid,
  Container,
  DownloadButton,
  GenerateButton,
  GenerateButtonContainer,
  ModalBox,
  WhiteButton,
} from './BackupCodes.styles';
import BackupCodesContainer from './components/BackupCodesContainer';
import PrintableBackupCodes from './components/PrintableBackupCodes';
import { BackupCodesProps } from './types';

const fileName = `scalar-backup-codes-${new Date().toLocaleDateString('en-US').replace(/\//g, '-')}.pdf`;

const BackupCodes = ({
  backupCodes = [],
  disabledGenerate = false,
  handleClose = () => {},
  handleGenerateNewCodes = () => {},
  isGeneratingBackupCodes = false,
  isModal = false,
  openModal = false,
  username = '',
}: BackupCodesProps) => {
  const [doneIsEnabled, setDoneIsEnabled] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const { processErrorResponse, successNotification } = useResponse();

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    onAfterPrint: () => setDoneIsEnabled(true),
  });

  const handleDownload = useReactToPrint({
    onPrintError: error =>
      processErrorResponse({
        error,
        defaultErrorMessage: ERROR_DOWNLOAD_CODES,
      }),
    contentRef: componentRef,
    preserveAfterPrint: true,
    print: async printIframe => {
      const document = printIframe.contentDocument;
      if (document) {
        const html = document.getElementsByTagName('html')[0];
        // eslint-disable-next-line new-cap
        const pdfExporter = new jsPDF();
        try {
          await pdfExporter.html(html, {
            callback(doc) {
              doc.save(fileName);
            },
            margin: [10, 10, 10, 10],
            autoPaging: 'text',
            x: 0,
            y: 0,
            width: 190,
            windowWidth: 675,
          });
          successNotification(SUCCESS_DOWNLOAD_CODES);
          setDoneIsEnabled(true);
        } catch (error) {
          processErrorResponse({
            error,
            defaultErrorMessage: ERROR_DOWNLOAD_CODES,
          });
        }
      }
    },
  });

  const ContainerComponent = isModal ? ModalBox : Container;

  return (
    <BackupCodesContainer handleClose={handleClose} isModal={isModal} openModal={openModal}>
      <ContainerComponent>
        {/* Only for Print */}
        <PrintableBackupCodes
          backupCodes={backupCodes}
          componentRef={componentRef}
          disabledGenerate={disabledGenerate}
          forPrint
          isGeneratingBackupCodes={isGeneratingBackupCodes}
          isModal
          username={username}
        />

        <PrintableBackupCodes
          backupCodes={backupCodes}
          disabledGenerate={disabledGenerate}
          isGeneratingBackupCodes={isGeneratingBackupCodes}
          isModal={isModal}
        />

        {/* Buttons */}
        {isModal ? (
          <Grid container>
            <GenerateButtonContainer container justifyContent="center" alignItems="center">
              <Grid item>
                <GenerateButton
                  color="primary"
                  disabled={disabledGenerate || isGeneratingBackupCodes}
                  onClick={handleGenerateNewCodes}
                  startIcon={<RefreshIcon />}
                  variant="contained">
                  {GENERATE_NEW_CODES}
                </GenerateButton>
              </Grid>
            </GenerateButtonContainer>

            <ButtonsGrid container justifyContent="space-between" alignItems="center">
              <Grid item>
                {isModal && (
                  <ActionButton color="primary" onClick={handleClose} variant="contained" disabled={!doneIsEnabled}>
                    {DONE_CODES}
                  </ActionButton>
                )}

                <DownloadButton
                  as={WhiteButton}
                  color="primary"
                  disabled={backupCodes.length === 0 || !username}
                  onClick={() => handleDownload()}
                  variant="contained">
                  {DOWNLOAD_CODES}
                </DownloadButton>
              </Grid>

              <Grid item>
                <WhiteButton
                  color="primary"
                  disabled={backupCodes.length === 0 || !username}
                  onClick={() => handlePrint()}
                  variant="contained">
                  {PRINT_CODES}
                </WhiteButton>
              </Grid>
            </ButtonsGrid>
          </Grid>
        ) : (
          <GenerateButtonContainer container justifyContent="flex-end" alignItems="center">
            <Grid item>
              <GenerateButton
                isMd
                color="primary"
                disabled={disabledGenerate || isGeneratingBackupCodes}
                onClick={handleGenerateNewCodes}
                startIcon={<RefreshIcon />}
                variant="contained">
                {GENERATE_NEW_CODES}
              </GenerateButton>
            </Grid>

            <Grid item>
              <DownloadButton
                as={WhiteButton}
                color="primary"
                disabled={backupCodes.length === 0 || !username}
                onClick={() => handleDownload()}
                variant="contained">
                {DOWNLOAD_CODES}
              </DownloadButton>
            </Grid>

            <Grid item>
              <DownloadButton
                as={WhiteButton}
                color="primary"
                disabled={backupCodes.length === 0 || !username}
                onClick={() => handlePrint()}
                variant="contained">
                {PRINT_CODES}
              </DownloadButton>
            </Grid>
          </GenerateButtonContainer>
        )}
      </ContainerComponent>
    </BackupCodesContainer>
  );
};

export default BackupCodes;
